import React, { useState, useEffect } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
    setOrderByDirection,
    setOrderByProperty,
    resetState,
    setActionMenuItems,
    addDetailsBreadcrumb,
    resetScroll,
    removeDetailsBreadcrumb
} from '@app/store/actions';
import { infiniteScrollItemsFetchLimit, fromNow, genericErrorToast, hasOrgPermission } from '@app/helpers';
import { useTypedTranslation, useSetPage } from '@app/hooks';
import { ITableHead, IListPageBarEnabled, IMessages, IDotMenu, OrderType, Order } from '@app/models';
import { Link, TableCell } from '@material-ui/core';
import { notificationService, basicService } from '@app/services';
import {
    PasslyTable
} from '@app/components';
import { confirm } from '@app/components/Modal/confirmModal';
import { showNeedToSelectAtleastOneModal } from '@app/helpers/dialogs.helper';
import { ManageAccount } from '@app/store/constants';

enum TableHeadEnum {
    sender = 'Sender',
    subject = 'Subject',
    created = 'Created'
}

interface IMessageTableData {
    checked: boolean;
    sender: string;
    subject: string;
    created: Date;
    id: string;
    important: boolean;
    isRead: boolean;
}

const AllNotificationsPage = () => {
    const [order, setOrder] = useState<OrderType>(Order.desc);
    const [orderBy, setOrderBy] = useState('Created');
    const [rows, setRows] = useState<IMessageTableData[]>([]);
    const loadingData = useSelector((state: any) => state.basic.loadingData);
    const [messageCount, setMessageCount] = useState(0);

    const dispatch = useDispatch();

    const { translation } = useTypedTranslation();

    const setListPageBarEnabledOptions: IListPageBarEnabled = {
        changeViewEnabled: false,
        filtersViewEnabled: false,
        orderByEnabled: true,
        organizationsEnabled: false,
        actionMenuEnabled: true
    };

    const getComplexSenderCell = (row: any) => {
        return (
            <TableCell>
                <div className='user__name'>
                    {row.important && <span className='notification__importantFlag'>!</span>}
                    <Link component='button' onClick={viewMessage(row.id)}>
                        {getMessageFrom(row.sender)}
                    </Link>
                </div>
            </TableCell>
        );
    };

    const getComplexSubjectCell = (row: any) => {
        return (
            <TableCell className={!row.isRead ? 'notification__unreadRow' : ''}>{row.subject}</TableCell>
        );
    };

    const getComplexCreatedCell = (row: any) => {
        return (
            <TableCell className={!row.isRead ? 'notification__unreadRow' : ''}>
                {!row.created ? <>{translation('DATE_NEVER')}</> : <>{fromNow(row.created)}</>}
            </TableCell>
        );
    };

    const headCells: ITableHead[] = [
        {
            id: TableHeadEnum.sender,
            label: 'MSG_SENDER',
            type: 'getComplexCell',
            width: '200px',
            getComplexCell: getComplexSenderCell
        },
        {
            id: TableHeadEnum.subject,
            label: 'MSG_SUBJECT',
            type: 'getComplexCell',
            getComplexCell: getComplexSubjectCell
        },
        {
            id: TableHeadEnum.created,
            label: 'MESSAGE_CREATED',
            type: 'getComplexCell',
            width: '200px',
            getComplexCell: getComplexCreatedCell
        }
    ];

    useSetPage(setListPageBarEnabledOptions, undefined);

    useEffect(() => {
        getRowDotMenu();
        dispatch(setActionMenuItems(actionMenuItems));
        dispatch(addDetailsBreadcrumb(translation('MY_MESSAGES')));

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    useEffect(() => {
        // need this to update the component when rows changes or the component won't have the new rows
        dispatch(setActionMenuItems(actionMenuItems));
    }, [rows]);

    const createSortHandler = (property: string) => () => {
        const ascDesc: OrderType = order === Order.asc ? Order.desc : Order.asc;
        dispatch(resetState());
        setOrder(ascDesc);
        setOrderBy(property);
        dispatch(setOrderByDirection(ascDesc));
        dispatch(setOrderByProperty(property));
    };

    const handleSuccess = (response: any) => {
        const count = response.data['@odata.count'];

        if (count !== undefined) {
            setMessageCount(count);
        }

        const results: IMessages[] = response.data.value;

        if (results) {
            let newRows = results.map((row: IMessages) => {
                const data: IMessageTableData = {
                    id: row.Id,
                    sender: row.Sender,
                    subject: row.Subject,
                    created: row.Created,
                    checked: false,
                    important: row.Important,
                    isRead: row.IsRead
                };

                return data;
            });

            if (results.length === 0) {
                setRows([]);
            } else if (count !== undefined) {
                setRows(newRows);
            } else {
                setRows(rows.concat(newRows));
            }
        }
    };

    const fetchMoreData = (skipCount: number, isReset: boolean) => {
        notificationService.getMessages(orderBy, order, infiniteScrollItemsFetchLimit, skipCount, isReset, genericErrorToast, handleSuccess);
    };

    const getMessageFrom = (sender: string) => {
        if (!sender) {
            return translation('SYSTEM_MSG_SENDER');
        }

        return sender;
    };

    const deleteRow = (e: any) => {
        const id: string = e.currentTarget.id;

        let jsonObject: any = {};
        jsonObject.Messages = [];
        jsonObject.Messages.push(id);

        deleteRows(jsonObject);
    };

    const deleteSelected = (e: any) => {
        let jsonObject: any = {};
        jsonObject.Messages = [];

        rows.forEach(function (row) {
            if (row.checked) {
                jsonObject.Messages.push(row.id);
            }
        });

        if (jsonObject.Messages.length === 0) {
            showNeedToSelectAtleastOneModal(translation);
            return;
        }

        deleteRows(jsonObject);
    };

    const deleteRows = (jsonObject: any) => {
        confirm(
            translation('DELETE_NOTIFICATION_BODY'),
            translation('DELETE'),
            translation('CANCEL'),
            translation('DELETE_NOTIFICATION_HEADER'),
            false,
            { isDeleteAction: true }
        ).then(
            () => {
                notificationService.delete(jsonObject, genericErrorToast, handleRefreshScreen);
            },
            () => {
                // do nothing on cancel
            }
        );
    };

    const readMessageRow = (e: any) => {
        const id: string = e.currentTarget.id;

        viewMessage(id)();
    };

    const viewMessage = (id: string) => () => {
        basicService.redirect('/profile/read-messages/details/' + id);
    };

    const markAsReadSelected = (e: any) => {
        let jsonObject: any = {};
        jsonObject.Messages = [];

        rows.forEach(function (row) {
            if (row.checked) {
                jsonObject.Messages.push(row.id);
            }
        });

        if (jsonObject.Messages.length === 0) {
            showNeedToSelectAtleastOneModal(translation);
            return;
        }

        markAsRead(jsonObject);
    };

    const markAsRead = (jsonObject: any) => {
        notificationService.markAsRead(jsonObject, genericErrorToast, handleRefreshScreen);
    };

    const markReadRow = (e: any) => {
        const id: string = e.currentTarget.id;

        let jsonObject: any = {};
        jsonObject.Messages = [];
        jsonObject.Messages.push(id);

        markAsRead(jsonObject);
    };

    const handleRefreshScreen = () => {
        setRows([]);
        dispatch(resetScroll());
    };

    const getRowDotMenu = () => {
        let rowDotMenu: IDotMenu[] = [];

        rowDotMenu.push({
            name: translation('READ_MESSAGE'),
            action: readMessageRow
        });

        rowDotMenu.push({
            name: translation('MARK_READ_MESSAGE'),
            action: markReadRow
        });

        rowDotMenu.push({
            name: translation('DELETE_USER_MESSAGE'),
            action: deleteRow
        });

        return rowDotMenu;
    };

    const actionMenuItems: IDotMenu[] = [
        {
            name: translation('MARK_READ_MESSAGE'),
            action: markAsReadSelected
        },
        {
            name: translation('DELETE_SELECTED_MESSAGES'),
            action: deleteSelected
        }
    ];

    return (
        <>
            <PasslyTable
                rows={rows}
                setRows={setRows}
                order={order}
                orderBy={orderBy}
                handleOrderChange={createSortHandler}
                headerItems={headCells}
                rowsCount={messageCount}
                onFetch={fetchMoreData}
                withCheckboxes={hasOrgPermission([ManageAccount])}
                actionMenuItems={actionMenuItems}
                emptyListHelper={translation('EMPTY_MESSAGE_LIST')}
                loadingData={loadingData}
                deleteRow={deleteRow}
            />
        </>
    );
};

export default AllNotificationsPage;
